import { AfterViewInit, Component, input, OnInit } from '@angular/core';
import * as echarts from 'echarts/core';
import moment from 'moment';

import type {
  ComposeOption
} from 'echarts/core';

import type {
  LineSeriesOption, BarSeriesOption, PieSeriesOption
} from 'echarts/charts';


import {
  LineChart, BarChart, PieChart
  // or BarChart, PieChart, etc.
} from 'echarts/charts';

import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  // etc.
} from 'echarts/components';

import {
  CanvasRenderer
} from 'echarts/renderers';

import { EChartsOption } from 'echarts/types/dist/shared';

echarts.use([
  LineChart,
  BarChart,
  PieChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  CanvasRenderer
]);

@Component({
  selector: 'app-insights-floors-report',
  standalone: false,
  templateUrl: './insights-floors-report.component.html',
  styleUrl: './insights-floors-report.component.css',
})
export class InsightsFloorsReportComponent implements OnInit, AfterViewInit {
  reportData = input.required<any>();
  chartInstance?: echarts.ECharts;
  floorChartsTotal: FloorChartOption[] = [];
  floorChartsWorking: FloorChartOption[] = [];
  floorChartsHours: FloorChartOption[] = [];

  constructor() { }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.floorChartsTotal = this.generateFloorCharts(this.reportData().totalResults);
      this.floorChartsWorking = this.generateWorkingFloorCharts(this.reportData().workingResults);
      this.floorChartsHours = this.generateHourlyFloorCharts(this.reportData().hourlyResults);

      this.generateChartTotalsFor(this.floorChartsTotal[0]);
      this.generateChartWorkingFor(this.floorChartsWorking[0]);
      this.generateChartHoursFor(this.floorChartsHours[0]);
    }, 100);
  }

  ngOnInit(): void {

  }

  generateChartTotalsFor(chart: FloorChartOption) {
    const chartDom = document.getElementById('main');
    this.chartInstance = echarts.init(chartDom);
    this.chartInstance.setOption(chart.option);
  }

  generateChartWorkingFor(chart: FloorChartOption) {
    const chartDom = document.getElementById('mainw');
    this.chartInstance = echarts.init(chartDom);
    this.chartInstance.setOption(chart.option);
  }

  generateChartHoursFor(chart: FloorChartOption) {
    const chartDom = document.getElementById('mainh');
    this.chartInstance = echarts.init(chartDom);
    this.chartInstance.setOption(chart.option);
  }

  generateHourlyFloorCharts(hourlyResults: any): FloorChartOption[] {
    const results = hourlyResults || {};
    const floors = Object.keys(results).map(Number).sort((a, b) => a - b);
    const charts: FloorChartOption[] = [];

    floors.forEach(floor => {
      const floorData = results[floor];
      const p1Data = Array(24).fill(0); // Hours 0-23
      const p2Data = Array(24).fill(0);
      floorData.forEach((d: any) => {
        const hour = d.hour;
        if (d.period === 'Before') p1Data[hour] = d.avgMinutesPerHour;
        else if (d.period === 'After') p2Data[hour] = d.avgMinutesPerHour;
      });
      const peakReduction = Math.max(...p1Data) - Math.max(...p2Data); // e.g., mid-day drop

      const option: EChartsOption = {
        title: {
          text: `Floor ${floor} - Minutes Cooling per Hour`,
          left: 'center',
          textStyle: { fontSize: 16, fontWeight: 'bold' }
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params: any[]) => {
            const hour = params[0].name;
            const p1 = params.find(p => p.seriesName === 'P1')?.value || 0;
            const p2 = params.find(p => p.seriesName === 'P2')?.value || 0;
            return `Hour ${hour}:00<br/>P1: ${p1.toFixed(1)} min/hr | P2: ${p2.toFixed(1)} min/hr`;
          }
        },
        legend: { data: ['P1 (16 May - 30 Jun)', 'P2 (1 Jul - 15 Aug)'], top: '8%' },
        xAxis: {
          type: 'category',
          data: Array.from({ length: 24 }, (_, i) => i), // 0-23
          name: 'Hour of day',
          axisLabel: { fontSize: 10, rotate: 0 }
        },
        yAxis: {
          type: 'value',
          name: 'Minutes per hour',
          min: 0,
          max: 50,
          interval: 5,
          axisLabel: { fontSize: 12 }
        },
        series: [
          {
            name: 'P1 (16 May - 30 Jun)',
            type: 'line',
            data: p1Data,
            itemStyle: { color: '#FF8C00' }, // Blue
            lineStyle: { type: 'solid', width: 2 }
          },
          {
            name: 'P2 (1 Jul - 15 Aug)',
            type: 'line',
            data: p2Data,
            itemStyle: { color: '#00a2ffff' }, // Orange
            lineStyle: { type: 'dashed', width: 2 }
          }
        ],
        grid: { left: '10%', right: '10%', bottom: '15%', top: '15%' }
      };

      charts.push({ floor, option, p1Avg: p1Data.reduce((a, b) => a + b, 0) / 24, p2Avg: p2Data.reduce((a, b) => a + b, 0) / 24, reductionPercent: peakReduction });
    });

    return charts;
  }

  generateWorkingFloorCharts(workingResults: any): FloorChartOption[] {
    const results = workingResults || {};
    const floors = Object.keys(results).map(Number).sort((a, b) => a - b);
    const charts: FloorChartOption[] = [];

    floors.forEach(floor => {
      const floorData = results[floor];
      const p1Working = floorData.find((d: any) => d.period === 'Before' && d.sub_period === 'Working')?.avgDailyHours || 0;
      const p1Non = floorData.find((d: any) => d.period === 'Before' && d.sub_period === 'Non-Working')?.avgDailyHours || 0;
      const p2Working = floorData.find((d: any) => d.period === 'After' && d.sub_period === 'Working')?.avgDailyHours || 0;
      const p2Non = floorData.find((d: any) => d.period === 'After' && d.sub_period === 'Non-Working')?.avgDailyHours || 0;
      const reduction = ((p1Working - p2Working) / p1Working * 100).toFixed(1) || 0; // Working-focused %

      const option: EChartsOption = {
        title: {
          text: `Floor ${floor} - Working vs Non-Working Hours`,
          left: 'center',
          textStyle: { fontSize: 16, fontWeight: 'bold' }
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params: any[]) => {
            const label = params[0].name;
            const value = params[0].value;
            const series = params[0].seriesName;
            return `${label} - ${series}: ${value.toFixed(2)}h/day`;
          }
        },
        legend: { data: ['Working', 'Non-Working'], top: '8%' },
        xAxis: {
          type: 'category',
          data: ['P1 Working', 'P1 Non-Working', '', '', 'P2 Working', 'P2 Non-Working'],
          axisLabel: { fontSize: 10, rotate: 45 } // Rotated for fit
        },
        yAxis: {
          type: 'value',
          name: 'Hours per day',
          min: -5, // For negatives
          max: 20,
          interval: 2.5,
          axisLabel: { fontSize: 12 }
        },
        series: [
          {
            name: 'Working',
            type: 'bar',
            data: [p1Working, 0, 0, 0, p2Working, 0],
            itemStyle: { color: '#04a334ff' }, // Solid orange
            barWidth: 60 // Wider
          },
          {
            name: 'Non-Working',
            type: 'bar',
            data: [0, p1Non, 0, 0, 0, p2Non],
            itemStyle: { color: '#FF8C00' }, // Lighter orange
            barWidth: 60
          }
        ],
        grid: { left: '8%', right: '5%', bottom: '20%', top: '20%' } // Extra space for labels
      };

      charts.push({ floor, option, p1Avg: p1Working + p1Non, p2Avg: p2Working + p2Non, reductionPercent: +reduction });
    });

    return charts;
  }

  generateFloorCharts(results: any): FloorChartOption[] {

    const floors = Object.keys(results).map(Number).sort((a, b) => a - b); // Floors 2-7
    const charts: FloorChartOption[] = [];

    floors.forEach(floor => {
      const floorData = results[floor];
      const before = floorData.find((d: any) => d.period === 'Before')?.avgDailyHours || 0;
      const after = floorData.find((d: any) => d.period === 'After')?.avgDailyHours || 0;
      const reduction = ((before - after) / before * 100).toFixed(1);

      const option: EChartsOption = {
        title: {
          text: `Floor ${floor} - Avg Daily Inferred AC Hours`,
          left: 'center',
          textStyle: { fontSize: 16, fontWeight: 'bold' }
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params: any[]) => {
            const period = params[0].name;
            const value = params[0].value;
            const periodLabel = period === 'P1' ? 'Before (May-Jun)' : 'After (Jul-Aug)';
            return `${periodLabel}<br/>Avg Daily Hours: ${value.toFixed(2)}`;
          }
        },
        xAxis: {
          type: 'category',
          data: ['P1', 'P2'],
          axisLabel: { fontSize: 12 }
        },
        yAxis: {
          type: 'value',
          name: 'Hours per day',
          min: 0,
          max: 20,
          interval: 2,
          axisLabel: { fontSize: 12 }
        },
        series: [
          {
            name: 'Inferred AC Hours',
            type: 'bar',
            data: [before, after],
            itemStyle: { color: '#FF8C00' }, // Orange bars
            barWidth: 150
          }
        ],
        grid: { left: '10%', right: '10%', bottom: '15%', top: '15%' }
      };

      charts.push({ floor, option, p1Avg: before, p2Avg: after, reductionPercent: +reduction });
    });

    return charts;
  }
}

export interface FloorChartOption {
  floor: number;
  option: EChartsOption;
  p1Avg: number;
  p2Avg: number;
  reductionPercent: number;
}
